// axiosConfig.js — Centralised Axios Configuration
//
// Owns the transport layer only: the axios instance and its interceptors.
// Endpoint functions live in ./api.js and import the instance from here.
//
// Split out from api.js so tests can substitute a fake transport while still
// exercising the real endpoint functions — several of them build query params
// and throw on missing arguments, which is behaviour worth testing.
// Mirrors the shared/config/cachedAxiosConfig.js pattern in ecoh-ui-module.

import axios from 'axios';
import { getToken, endSession, isExpired } from '../auth/session';

/**
 * Marks a request as one nobody asked for — an auto-refresh, a poll, a prefetch.
 *
 *   getIncidents(background())
 *
 * The distinction matters only to the 401 handler below. Everything else about
 * the request is unchanged.
 */
export const background = (config = {}) => ({
  ...config,
  meta: { ...config.meta, background: true },
});

// ============================================
// Request timeout
// ============================================
// Without one, axios waits forever. A request that never answers leaves the
// button that started it on "Saving..." for the rest of the session: every
// handler re-enables its button in a `finally`, and a promise that never
// settles never reaches it. There is no error, no retry and no way out short of
// reloading the page.
//
// 25 seconds because API Gateway abandons its own integration at 29, so waiting
// longer than that can only ever end in the gateway's 504 — and a cold start on
// this stack takes around 8 seconds, so 25 leaves ample room for a request that
// is slow but working.
const REQUEST_TIMEOUT_MS = 25_000;

// ============================================
// Axios instance — all API calls go through here
// ============================================
const api = axios.create({
  // Route through CloudFront, never straight to execute-api. The distribution
  // injects the x-origin-secret origin header, applies the edge WAF's managed
  // rule groups and rate limit, and disables caching; a direct execute-api call
  // gets none of that. This fallback is the real config whenever the build does
  // not inject REACT_APP_API_BASE_URL, so pointing it back at execute-api
  // silently bypasses all of the above.
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.finops.devops.tonicsuite.io',
  headers: { 'Content-Type': 'application/json' },
  timeout: REQUEST_TIMEOUT_MS,
});

// Attach JWT token to every outgoing request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.debug('[API Request]', config.method?.toUpperCase(), config.url, 'Auth:', config.headers.Authorization ? 'Bearer ***' : 'MISSING');
  return config;
});

/**
 * Whether a 401 should end the session.
 *
 * It always used to. That meant a single 401 from a background poll — the
 * incidents list refreshes every 30 seconds without being asked — wiped the
 * session and redirected, throwing away whatever the person was in the middle
 * of typing. From their seat the page simply refreshed itself, which is how it
 * came to be reported as a random refresh rather than as a logout.
 *
 * A 401 is not proof that a session has ended. It can equally be one endpoint
 * validating tokens differently from the rest, or a token being renewed at that
 * moment, or a momentary server fault. So:
 *
 *   - a request the user made       → end the session; they acted, they get told
 *   - a poll nobody asked for       → only when the session has genuinely expired
 *
 * The cost of the second rule is that a truly dead session survives until the
 * next real interaction, which then ends it properly. That is the right trade:
 * a stale list is recoverable, half a post-incident review is not.
 */
const shouldEndSession = (config) => !config?.meta?.background || isExpired();

// Handle 401 globally — clear session and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && shouldEndSession(error.config)) {
      endSession();
      setTimeout(() => window.dispatchEvent(new Event('session-expired')), 0);
    }
    return Promise.reject(error);
  }
);

// ============================================
// GitHub Pull Requests — separate instance (localhost:8080)
// ============================================
const githubApi = axios.create({
  baseURL: process.env.REACT_APP_GITHUB_API_BASE_URL || 'http://localhost:8080',
  headers: { 'Content-Type': 'application/json' },
  timeout: REQUEST_TIMEOUT_MS,
});

githubApi.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export { api, githubApi };
export default api;
